import { useEffect, useState, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import useWindowDimensions from "../../utils/hooks/useWindowDimensions";
import { getUrlParams } from "../../utils";
import { aoiActions, layerActions, uiActions } from "../../store";

export const ViewStateContext = createContext({
  viewState: null,
  setViewState: () => {},
});

// updates redux store based on params present in url's parameter string
const UrlParser = (props) => {
  const layerState = useSelector((state) => state.layers);
  const dispatch = useDispatch();

  const { height, width } = useWindowDimensions();
  const [viewState, setViewState] = useState({
    width,
    height,
    latitude: 37.09024,
    longitude: -105.712891,
    zoom: 3,
    minZoom: 1.5,
    maxZoom: 20,
    bearing: 0,
    pitch: 0,
  });

  // parse url on initial render only
  useEffect(() => {
    let params = null;
    if (window.location.search.length > 0) {
      try {
        params = getUrlParams();
      } catch (e) {
        console.log(
          "malformatted URL. Try navigating to http://ngwos-app-dev.s3-website.us-west-2.amazonaws.com/"
        );
        console.log(e);
      }
    }

    updateMainAppState(params);
  }, []);

  // helper function to update redux store with params from url (main app)
  const updateMainAppState = (params) => {
    if (params) {
      const {
        aoiCategory,
        aoiSelectedIds,
        lat,
        lon,
        zoom,
        activeLayers,
        colorValues,
        activeFilters,
        selectedTab,
      } = params;

      // validate lat/lon/zoom
      const parsedLat = lat < 90 && lat > -90 ? Number(lat) : null;
      const parsedLon = lon < 180 && lon > -180 ? Number(lon) : null;
      const parsedZoom = zoom < 21 && zoom > 1.5 ? Number(zoom) : null;

      if (parsedLat && parsedLon && parsedZoom) {
        setViewState((prevState) => {
          return {
            ...prevState,
            longitude: parsedLon,
            latitude: parsedLat,
            zoom: parsedZoom,
          };
          // transitionDuration: 1000,
          // transitionInterpolator: new LinearInterpolator(),
        });
      }

      if (aoiCategory && aoiSelectedIds && aoiSelectedIds.length > 0) {
        dispatch(
          aoiActions.setSelection({
            catId: aoiCategory,
            selectedIds: aoiSelectedIds,
          })
        );
      }

      let activeLayersParsed = activeLayers;
      let colorValuesParsed = colorValues;

      // "none" indicates no active layers
      // otherwise, if empty array, load initial state
      if (activeLayersParsed[0] === "none") {
        dispatch(layerActions.setActiveLayers({ activeLayers: [] }));
      } else if (activeLayersParsed.length > 0) {
        dispatch(
          layerActions.setActiveLayers({
            activeLayers: activeLayersParsed,
          })
        );
      }

      // active layer & color value validation logic
      if (
        activeLayersParsed[0] !== "none" &&
        activeLayersParsed[0] !== "" &&
        colorValuesParsed.length > 0
      ) {
        let layersValid = true;
        let colorsValid = true;
        activeLayersParsed.forEach((parsedId, index) => {
          if (!Object.keys(layerState).includes(parsedId)) {
            layersValid = false;
          } else {
            const parsedColorValue = colorValuesParsed[index];
            const colorValueOptions = layerState[parsedId].properties
              .filter((property) => property.colorable)
              .map((property) => property.id);
            colorsValid = colorValueOptions.includes(parsedColorValue);
          }
        });

        // update redux store
        if (layersValid && colorsValid) {
          activeLayersParsed.forEach((id, index) => {
            dispatch(
              layerActions.changeColorValue({
                id: id,
                colorValue: colorValuesParsed[index],
              })
            );
          });
        }

        // parse & set active filters on each active layer
        if (layersValid) {
          // parse
          if (activeFilters) {
            let activeFiltersParsed = [...activeFilters].map(f => f)
            try {
              activeFiltersParsed = JSON.parse(`[${activeFiltersParsed}]`);
            } catch (e) {
              console.log(e);
            }

            // set
            activeLayersParsed.forEach((id, index) => {
              dispatch(
                layerActions.setFilters({
                  id: id,
                  newActiveFilters: activeFiltersParsed[index],
                })
              );
            });
          }
        }
      }

      if (selectedTab) {
        dispatch(uiActions.setSelectedTab(selectedTab));
      }
    }
  };

  return (
    <ViewStateContext.Provider value={{ viewState, setViewState }}>
      {props.children}
    </ViewStateContext.Provider>
  );
};

export default UrlParser;
